import { Form, Input, Radio } from "antd"
import { type UploadFile } from "antd/es/upload"
import Dragger from "antd/es/upload/Dragger"
import React, { useEffect, useState } from "react"

import draggerFunctions from "../../../helpers/draggerFunctions"
import { onRequiredTypeChange } from "../../../helpers/format"
import is from "../../../helpers/is"
import { type ApplicationState } from "../../../store"
import DraggerInside from "../../DraggerInside"
import KintoButton, { KintoButtonThemes } from "../../KintoButton"

type OwnProps = {
  afterFinish?: (formData: ProblemOrMissingItemsFormData) => void
  handleReturn?: () => void
  defaults: ApplicationState["onefleetdevolucao"]
}

export interface ProblemOrMissingItemsFormData {
  carProblemDescription: string
  accessoriesStatus?: Record<number, number>
  images?: Record<number, UploadFile[]>
}

const ProblemOrMissingItemsForm = (props: OwnProps): React.ReactElement => {
  const [formData] = Form.useForm()
  const [substep, setSubstep] = useState(0)
  const [currentAccessory, setCurrentAccessory] = useState(0)
  const [accessoryImages, setAccessoryImages] = useState<UploadFile[]>([])
  const [accessoriesThatNeedImage, setAccessoriesThatNeedImage] = useState<number[][]>([])
  const [finalFormData, setFinalFormData] = useState<ProblemOrMissingItemsFormData>({
    carProblemDescription: "",
    accessoriesStatus: {},
    images: {}
  })

  useEffect(() => {
    if (substep === 1) {
      setAccessoryImages(props.defaults?.data.damagedOrMissingItems?.images?.[accessoriesThatNeedImage[currentAccessory][0]] ?? [])
      formData.setFieldValue("anyname", {
        file: (props.defaults?.data.damagedOrMissingItems?.images?.[accessoriesThatNeedImage[currentAccessory][0]] ?? [])[0],
        fileList: props.defaults?.data.damagedOrMissingItems?.images?.[accessoriesThatNeedImage[currentAccessory][0]] ?? []
      })
    }
  }, [currentAccessory, substep])

  function handleProblemOrMissingItemsFinish () {
    const data = formData.getFieldsValue() ?? {}

    const accessoriesStatus = Object.entries(data).reduce((prev, [key, value]) => {
      if (is.number(key)) return { ...prev, [key]: value }
      else return prev
    }, {})

    const newFinalForm = { ...finalFormData, accessoriesStatus, carProblemDescription: data.carProblemDescription }

    setFinalFormData(newFinalForm)
    const entries = Object.entries(data).filter((entry) => props.defaults.accessoryStatus.find(status => status.code === entry[1] && status.isAProblem)).map((entry) => [Number(entry[0]), props.defaults.accessoryStatus.find(status => status.code === entry[1])?.code ?? 1])

    if (!entries.length) handleFormFinish(newFinalForm)
    else {
      setAccessoriesThatNeedImage(entries)
      setSubstep(1)
      setCurrentAccessory(0)
    }
  }

  function handleAccessoryImageFinish () {
    const temp = { ...finalFormData, images: { ...finalFormData.images, [accessoriesThatNeedImage[currentAccessory][0]]: accessoryImages } }
    setFinalFormData(temp)
    if (currentAccessory === (accessoriesThatNeedImage.length - 1)) handleFormFinish(temp)
    else setCurrentAccessory(currentAccessory + 1)
  }

  function handleAccessoryImageReturn () {
    if (currentAccessory === 0) setSubstep(0)
    else setCurrentAccessory(currentAccessory - 1)
  }

  function handleFormFinish (customFinalData?: ProblemOrMissingItemsFormData) {
    if (props.afterFinish) {
      props.afterFinish(customFinalData ?? finalFormData)
    }
  }

  // const selectOptions: SelectProps["options"] = [
  //   { label: "Danificado", value: "Danificado" },
  //   { label: "Falta", value: "Falta" },
  //   { label: "n/a", value: "n/a" }
  // ]

  const defaultRadioGroup = (
    <Radio.Group>
      {
        props.defaults.accessoryStatus.map(status => (<Radio value={status.code} key={status.code}>{status.name}</Radio>))
      }
    </Radio.Group>)

  const accessories: Array<{ label: string, value: number }> = props.defaults.accessories.map(accessory => ({ label: accessory.name, value: accessory.code }))

  return (
    <>
      {
        substep === 0 && (
          <Form
            onFinish={handleProblemOrMissingItemsFinish}
            form={formData}
            layout="horizontal"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            initialValues={props.defaults?.data.images}
            requiredMark={onRequiredTypeChange}

          >
            <Form.ErrorList />

            {
              accessories.map(accessory => (
                <Form.Item
                  name={accessory.value}
                  label={accessory.label}
                  key={accessory.value}
                  initialValue={props.defaults.data.damagedOrMissingItems?.accessoriesStatus?.[accessory.value]}
                  rules={[
                    { required: true, message: "Campo obrigatório!" }
                  ]}
                >
                  {defaultRadioGroup}
                </Form.Item>

              ))
            }

            <Form.Item
              name = "carProblemDescription"
              label= "Caso necessário, descreva a avaria ou item faltante"
              initialValue={props.defaults?.data.damagedOrMissingItems?.carProblemDescription ?? ""}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              required={false}
            >
              <Input.TextArea size="large" placeholder="Descrição"/>
            </Form.Item>

            <Form.Item label="" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <KintoButton theme={KintoButtonThemes.outlined} onClick={props.handleReturn}>Voltar</KintoButton>
                <KintoButton theme={KintoButtonThemes.contained} htmlType="submit">Próximo</KintoButton>
              </div>
            </Form.Item>
          </Form>
        )
      }
      {
        substep === 1 && (
          <Form
            onFinish={handleAccessoryImageFinish}
            form={formData}
            layout="vertical"
            initialValues={props.defaults?.data.car}
            requiredMark={onRequiredTypeChange}
          >
            <Form.ErrorList />

            <Form.Item
              name="anyname"
              label={`Imagem do(a) ${accessories.find(accessory => accessory.value === accessoriesThatNeedImage[currentAccessory][0])?.label}${props.defaults.accessoryStatus.find(status => status.code === accessoriesThatNeedImage[currentAccessory][1])?.demandImage ? "" : " (Opcional)"}`}
              rules={[{ validator: async (_, value) => { if (!value?.fileList?.length && props.defaults.accessoryStatus.find(status => status.code === accessoriesThatNeedImage[currentAccessory][1])?.demandImage) throw new Error("Imagem não inserida!") } }]}
            >
              <Dragger
                accept=".png,.jpg,.jpeg"
                onChange={(info) => { draggerFunctions.handleImageChange(info, 1, setAccessoryImages) }}
                fileList={accessoryImages}
                customRequest={draggerFunctions.uploadFile}
                beforeUpload={draggerFunctions.beforeUploadFile}
              ><DraggerInside /></Dragger>
            </Form.Item>

            <Form.Item label="" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <KintoButton theme={KintoButtonThemes.outlined} onClick={handleAccessoryImageReturn}>Voltar</KintoButton>
                <KintoButton theme={KintoButtonThemes.contained} htmlType="submit">Próximo</KintoButton>
              </div>
            </Form.Item>
          </Form>
        )
      }
    </>
  )
}

export default ProblemOrMissingItemsForm
